import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";

import { TargetGroupTemplatesV3Api } from "apis/SchemesV3Api";
import { showToast } from "utils";
import TargetGroupBuilder, { buildAttributeFilters, mapAttributeFiltersToUi } from "./TargetGroupBuilder";
import { getInitialTargetGroup } from "./constants";

export default function TemplateFormModal({ open, onClose, brands, editingTemplate, defaultBrandId, onSaved }) {
  const [brandId, setBrandId] = useState(defaultBrandId || "");
  const [targetGroup, setTargetGroup] = useState(getInitialTargetGroup());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editingTemplate) {
      const tg = editingTemplate.targetGroup || editingTemplate;
      setBrandId(editingTemplate.brandId || editingTemplate.brand?.id || defaultBrandId || "");
      setTargetGroup({
        name: tg.name || editingTemplate.name || "",
        templateId: "",
        productIds: tg.productIds || [],
        products: (tg.products || []).length
          ? tg.products
          : (tg.productIds || []).map((id) => ({ id, primaryName: id, secondaryName: "" })),
        attributeFilters: mapAttributeFiltersToUi(tg.attributeFilters),
      });
    } else {
      setBrandId(defaultBrandId || "");
      setTargetGroup(getInitialTargetGroup());
    }
  }, [open, editingTemplate, defaultBrandId]);

  const handleSave = () => {
    if (!brandId) return showToast("Select a brand");
    if (!targetGroup.name?.trim()) return showToast("Template name is required");

    let attributeFilters;
    try {
      attributeFilters = buildAttributeFilters(targetGroup.attributeFilters);
    } catch (err) {
      return showToast(err.message);
    }

    const requestPayload = {
      brandId,
      name: targetGroup.name,
      productIds: targetGroup.productIds || [],
      ...(attributeFilters ? { attributeFilters } : {}),
    };

    setSaving(true);
    const apiCall = editingTemplate
      ? TargetGroupTemplatesV3Api.Update(editingTemplate.id, requestPayload)
      : TargetGroupTemplatesV3Api.Create(requestPayload);

    apiCall
      .then(() => {
        showToast(editingTemplate ? "Template updated" : "Template created", "success");
        onSaved();
        onClose();
      })
      .catch((err) => showToast(err?.response?.data?.message || "Error saving template"))
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editingTemplate ? "Edit Template" : "New Target Group Template"}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Brand</InputLabel>
            <Select label="Brand" value={brandId} onChange={(e) => setBrandId(e.target.value)} disabled={!!editingTemplate}>
              {brands.map((b) => (
                <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <TargetGroupBuilder
          sessionKey={`template-${open}`}
          targetGroup={targetGroup}
          brandId={brandId}
          templates={[]}
          showRemove={false}
          onChange={setTargetGroup}
          onRemove={() => {}}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {editingTemplate ? "Save Changes" : "Create Template"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
